import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { capture } from '../lib/posthog';
import { db } from '../lib/firebase';
import { useActivationChecklist, isAccountSynchronizationDone } from './ActivationChecklistContext';
import { useAccount } from './AccountContext';
import { useAuth } from './AuthContext';
import { shouldForceTrialDemarrage, trialNeedsAccountConnection } from '../utils/trialDemarrageRedirect';
import { useMobileViewport } from '../hooks/useMobileViewport';

const DEMO_ACTIVE_KEY_PREFIX = 'onboardingIaDemoActive';
const DEMO_PAGES_KEY_PREFIX = 'onboardingDemoPagesVisited';
const DEMO_EXPLICIT_KEY_PREFIX = 'onboardingDemoExplicit';
const DEMO_EXPLORATION_KEY_PREFIX = 'onboardingDemoExplorationKeys';
const DEMO_WALL_SHOWN_KEY_PREFIX = 'onboardingDemoWallShown';
/** Anciennes clés globales (non scopées par uid) — à nettoyer */
const LEGACY_DEMO_ACTIVE_KEY = 'onboardingIaDemoActive';
const LEGACY_DEMO_PAGES_KEY = 'onboardingDemoPagesVisited';
const PAGES_BEFORE_LOCK = 2;

/** Pages jamais comptées dans le quota d'exploration (2 pages avant mur démo). */
const SIGNUP_EXEMPT_PATHS = [
  '/demarrage',
  '/onboarding',
  '/register',
  '/login',
  '/email-verification',
  '/email-verified',
  '/email-confirmed',
  '/email-verification-handler',
  '/settings',
  '/ctrader-logout',
] as const;

function demoActiveKey(uid: string) {
  return `${DEMO_ACTIVE_KEY_PREFIX}_${uid}`;
}

function demoPagesKey(uid: string) {
  return `${DEMO_PAGES_KEY_PREFIX}_${uid}`;
}

function demoExplicitKey(uid: string) {
  return `${DEMO_EXPLICIT_KEY_PREFIX}_${uid}`;
}

function demoExplorationStorageKey(uid: string) {
  return `${DEMO_EXPLORATION_KEY_PREFIX}_${uid}`;
}

function demoWallShownStorageKey(uid: string) {
  return `${DEMO_WALL_SHOWN_KEY_PREFIX}_${uid}`;
}

function clearLegacyDemoSessionKeys() {
  sessionStorage.removeItem(LEGACY_DEMO_ACTIVE_KEY);
  sessionStorage.removeItem(LEGACY_DEMO_PAGES_KEY);
}

function readPagesVisited(uid: string | undefined): number {
  if (!uid) return 0;
  const raw = sessionStorage.getItem(demoPagesKey(uid));
  const parsed = raw ? parseInt(raw, 10) : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

function readExplorationKeys(uid: string): Set<string> {
  const raw = sessionStorage.getItem(demoExplorationStorageKey(uid));
  if (!raw) return new Set();

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((entry): entry is string => typeof entry === 'string'));
  } catch {
    return new Set();
  }
}

function persistExplorationKeys(uid: string, keys: Set<string>) {
  sessionStorage.setItem(demoExplorationStorageKey(uid), JSON.stringify([...keys]));
}

function isExemptExplorationPathname(pathname: string): boolean {
  return (SIGNUP_EXEMPT_PATHS as readonly string[]).includes(pathname);
}

function isExemptExplorationPath(pathname: string, search: string): boolean {
  if (isExemptExplorationPathname(pathname)) return true;
  // Analyse IA fictive : les onglets sont comptés séparément
  if (pathname === '/ia-analysis' && new URLSearchParams(search).get('onboardingDemo') === 'true') {
    return true;
  }
  return false;
}

function sanitizeExplorationKeys(keys: Set<string>): Set<string> {
  const sanitized = new Set<string>();
  for (const key of keys) {
    if (key.startsWith('path:')) {
      const pathname = key.slice('path:'.length);
      if (isExemptExplorationPathname(pathname)) continue;
    }
    sanitized.add(key);
  }
  return sanitized;
}

function isDemoAnalysisRoute(pathname: string, search: string): boolean {
  return pathname === '/ia-analysis' && new URLSearchParams(search).get('onboardingDemo') === 'true';
}

function hasExplicitDemoStart(uid: string): boolean {
  const key = demoExplicitKey(uid);
  return sessionStorage.getItem(key) === 'true' || localStorage.getItem(key) === 'true';
}

function markExplicitDemoStart(uid: string) {
  const key = demoExplicitKey(uid);
  sessionStorage.setItem(key, 'true');
  localStorage.setItem(key, 'true');
}

interface OnboardingDemoContextType {
  isActive: boolean;
  isLocked: boolean;
  isLockResolved: boolean;
  pagesVisited: number;
  wallVisible: boolean;
  startDemo: () => void;
  endDemo: () => void;
  unlockDemo: () => Promise<void>;
  connectAccount: () => void;
  importFile: () => void;
}

const OnboardingDemoContext = createContext<OnboardingDemoContextType | undefined>(undefined);

export function OnboardingDemoProvider({ children }: { children: ReactNode }) {
  const {
    user,
    hasActiveTrialOnboarding,
    onboardingDemoLocked,
    patchOnboardingDemoLocked,
  } = useAuth();
  const { checklistState } = useActivationChecklist();
  const { accounts, accountsLoading } = useAccount();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobileViewport = useMobileViewport();
  const countedExplorationsRef = useRef<Set<string>>(new Set());
  const lockWriteInFlightRef = useRef(false);
  const healLockAttemptedRef = useRef<string | null>(null);
  const wallShownCapturedRef = useRef(false);
  const uid = user?.uid;

  // Flag brut restauré depuis sessionStorage / posé par startDemo. Ne jamais
  // l'exposer tel quel : voir `isActive` plus bas, subordonné à hasSyncedAccount.
  const [isDemoSessionActive, setIsActive] = useState(false);
  const [pagesVisited, setPagesVisited] = useState(0);

  const isLockResolved = !uid || onboardingDemoLocked !== null;
  const isLocked = onboardingDemoLocked === true;
  const isConnectFlow = location.pathname === '/demarrage'
    && new URLSearchParams(location.search).get('connect') === '1';
  const needsConnectWall = shouldForceTrialDemarrage(
    hasActiveTrialOnboarding,
    checklistState.synchronisation,
    isMobileViewport,
  );
  const wallVisibleRaw = isLockResolved && isLocked && !isConnectFlow && needsConnectWall;
  const shouldTrackExploration = needsConnectWall;
  // Indépendant du device (contrairement à needsConnectWall) : sert à router un trial
  // sans compte connecté vers la démo IA, y compris sur mobile où le mur est désactivé.
  const needsAccountConnection = trialNeedsAccountConnection(
    hasActiveTrialOnboarding,
    checklistState.synchronisation,
  );
  // Vérité terrain indépendante de la checklist : `checklistState.synchronisation` peut
  // rester bloqué à une valeur périmée (cliquet à sens unique désactivé une fois le
  // checklist marqué "complété", cf. ActivationChecklistContext) alors que le compte est
  // réellement synchronisé. Un user avec un compte synchronisé — donc une vraie analyse IA
  // possible — ne doit jamais être renvoyé vers la démo, quoi que dise la checklist.
  const hasSyncedAccount = accounts.some((account) =>
    isAccountSynchronizationDone(account as unknown as Record<string, unknown>),
  );
  // La démo IA fictive n'existe que pour un utilisateur SANS compte synchronisé.
  // Le flag de session (sessionStorage) survit à la connexion du compte quand elle
  // se fait hors de /demarrage (OAuth broker, autre onglet, page connexions…) : sans
  // cette subordination, /ia-analysis restait bloqué sur l'analyse fictive alors que
  // le compte réel avait des trades. hasSyncedAccount est la seule vérité terrain.
  const isActive = isDemoSessionActive && !hasSyncedAccount;

  // Stabilise l'affichage du mur : les sources amont (verrou Firestore, checklist)
  // peuvent changer de valeur plusieurs fois en rafale avant de se stabiliser.
  // On affiche immédiatement mais on ne masque qu'après un court délai sans
  // redevenir vrai entre-temps, pour éviter un clignotement du mur.
  const [wallVisible, setWallVisible] = useState(wallVisibleRaw);
  const wallHideTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (wallVisibleRaw) {
      if (wallHideTimeoutRef.current !== null) {
        window.clearTimeout(wallHideTimeoutRef.current);
        wallHideTimeoutRef.current = null;
      }
      setWallVisible(true);
      return;
    }

    wallHideTimeoutRef.current = window.setTimeout(() => {
      wallHideTimeoutRef.current = null;
      setWallVisible(false);
    }, 600);

    return () => {
      if (wallHideTimeoutRef.current !== null) {
        window.clearTimeout(wallHideTimeoutRef.current);
        wallHideTimeoutRef.current = null;
      }
    };
  }, [wallVisibleRaw]);

  const clearDemoSessionForUid = useCallback((targetUid: string | undefined) => {
    if (!targetUid) return;
    sessionStorage.removeItem(demoActiveKey(targetUid));
    sessionStorage.removeItem(demoPagesKey(targetUid));
    sessionStorage.removeItem(demoExplorationStorageKey(targetUid));
    clearLegacyDemoSessionKeys();
  }, []);

  const persistLock = useCallback(async (_visitedCount: number) => {
    if (!uid || lockWriteInFlightRef.current || onboardingDemoLocked === true) return;
    if (!shouldForceTrialDemarrage(hasActiveTrialOnboarding, checklistState.synchronisation, isMobileViewport)) return;

    lockWriteInFlightRef.current = true;
    try {
      await updateDoc(doc(db, 'users', uid), { onboardingDemoLocked: true });
    } catch (error) {
      console.error('[OnboardingDemo] persistLock failed:', error);
    } finally {
      lockWriteInFlightRef.current = false;
    }
  }, [
    uid,
    onboardingDemoLocked,
    hasActiveTrialOnboarding,
    checklistState.synchronisation,
    isMobileViewport,
  ]);

  const unlockDemo = useCallback(async () => {
    if (uid) {
      clearDemoSessionForUid(uid);
      localStorage.removeItem(demoExplicitKey(uid));
    }
    setIsActive(false);
    setPagesVisited(0);
    countedExplorationsRef.current = new Set();

    if (!uid) {
      patchOnboardingDemoLocked(false);
      return;
    }

    if (onboardingDemoLocked === false) {
      patchOnboardingDemoLocked(false);
      return;
    }

    try {
      await updateDoc(doc(db, 'users', uid), { onboardingDemoLocked: false });
    } catch (error) {
      console.error('[OnboardingDemo] unlockDemo failed:', error);
    }
  }, [uid, onboardingDemoLocked, patchOnboardingDemoLocked, clearDemoSessionForUid]);

  const endDemo = useCallback(() => {
    clearDemoSessionForUid(uid);
    setIsActive(false);
    setPagesVisited(0);
    countedExplorationsRef.current = new Set();
  }, [uid, clearDemoSessionForUid]);

  const startDemo = useCallback(() => {
    // Un compte synchronisé rend la démo caduque : refuser de la (re)démarrer,
    // même via l'URL ?onboardingDemo=true ou un appel tardif de la page IA.
    if (!uid || hasSyncedAccount) return;

    markExplicitDemoStart(uid);
    sessionStorage.setItem(demoActiveKey(uid), 'true');
    sessionStorage.setItem(demoPagesKey(uid), '0');
    sessionStorage.removeItem(demoExplorationStorageKey(uid));
    clearLegacyDemoSessionKeys();
    setIsActive(true);
    setPagesVisited(0);
    countedExplorationsRef.current = new Set();
    capture('onboarding_demo_started');
  }, [uid, hasSyncedAccount]);

  const connectAccount = useCallback(() => {
    capture('onboarding_demo_connect_clicked', { pages_visited: pagesVisited, locked: isLocked });
    navigate('/demarrage?connect=1&open=auto');
  }, [navigate, pagesVisited, isLocked]);

  const importFile = useCallback(() => {
    capture('onboarding_demo_import_clicked', { pages_visited: pagesVisited, locked: isLocked });
    navigate('/demarrage?connect=1&open=import');
  }, [navigate, pagesVisited, isLocked]);

  const registerExploration = useCallback((explorationKey: string) => {
    if (!uid || isLocked || !shouldTrackExploration) return;
    if (countedExplorationsRef.current.has(explorationKey)) return;

    countedExplorationsRef.current.add(explorationKey);
    persistExplorationKeys(uid, countedExplorationsRef.current);

    const nextCount = countedExplorationsRef.current.size;
    sessionStorage.setItem(demoPagesKey(uid), String(nextCount));
    setPagesVisited(nextCount);

    if (nextCount >= PAGES_BEFORE_LOCK) {
      void persistLock(nextCount);
    }
  }, [uid, isLocked, shouldTrackExploration, persistLock]);

  // Firestore = source de vérité pour le verrou
  useEffect(() => {
    if (!uid) return;

    return onSnapshot(
      doc(db, 'users', uid),
      (snap) => {
        const locked = snap.data()?.onboardingDemoLocked === true;
        patchOnboardingDemoLocked(locked);
      },
      (error) => {
        console.error('[OnboardingDemo] lock snapshot failed:', error);
      },
    );
  }, [uid, patchOnboardingDemoLocked]);

  // Restaurer le compteur d'exploration scopé par utilisateur
  useEffect(() => {
    clearLegacyDemoSessionKeys();

    if (!uid) {
      setIsActive(false);
      setPagesVisited(0);
      countedExplorationsRef.current = new Set();
      return;
    }

    setIsActive(sessionStorage.getItem(demoActiveKey(uid)) === 'true');
    const sanitizedKeys = sanitizeExplorationKeys(readExplorationKeys(uid));
    countedExplorationsRef.current = sanitizedKeys;
    persistExplorationKeys(uid, sanitizedKeys);
    sessionStorage.setItem(demoPagesKey(uid), String(sanitizedKeys.size));
    setPagesVisited(sanitizedKeys.size);
  }, [uid]);

  // Reprend le verrou si l'exploration était déjà complète avant reload
  useEffect(() => {
    if (!uid || onboardingDemoLocked === true || onboardingDemoLocked === null) return;
    if (!shouldForceTrialDemarrage(hasActiveTrialOnboarding, checklistState.synchronisation, isMobileViewport)) return;

    const visited = readPagesVisited(uid);
    if (visited >= PAGES_BEFORE_LOCK) {
      void persistLock(visited);
    }
  }, [
    uid,
    onboardingDemoLocked,
    persistLock,
    hasActiveTrialOnboarding,
    checklistState.synchronisation,
    isMobileViewport,
  ]);

  // Corrige un verrou erroné : locked en base avec moins de 2 pages explorées.
  // healLockAttemptedRef ne doit JAMAIS être réarmé tant que l'uid ne change pas :
  // en multi-onglets, un onglet avec peu de pages (compteur local sessionStorage,
  // non partagé) peut sans cesse déverrouiller un verrou qu'un autre onglet
  // (avec 2+ pages) reverrouille aussitôt — un ping-pong Firestore qui fait
  // clignoter le mur et spam l'event onboarding_demo_wall_shown.
  useEffect(() => {
    if (!uid || onboardingDemoLocked !== true) return;

    if (!needsConnectWall) {
      if (healLockAttemptedRef.current !== uid) {
        healLockAttemptedRef.current = uid;
        void unlockDemo();
      }
      return;
    }

    if (healLockAttemptedRef.current === uid) return;

    const visited = Math.max(readPagesVisited(uid), countedExplorationsRef.current.size);
    if (visited >= PAGES_BEFORE_LOCK) return;

    healLockAttemptedRef.current = uid;
    void unlockDemo();
  }, [uid, onboardingDemoLocked, needsConnectWall, unlockDemo]);

  // Purge un flag démo périmé : le compte est synchronisé mais sessionStorage
  // (et le state) portent encore la démo. Sans ça, le flag est restauré à chaque
  // montage et ressuscite l'analyse fictive sur /ia-analysis.
  useEffect(() => {
    if (!uid || accountsLoading || !hasSyncedAccount || !isDemoSessionActive) return;
    endDemo();
    localStorage.removeItem(demoExplicitKey(uid));
  }, [uid, accountsLoading, hasSyncedAccount, isDemoSessionActive, endDemo]);

  // Démarrage démo IA fictive via CTA ou route dédiée
  useEffect(() => {
    if (!isDemoAnalysisRoute(location.pathname, location.search)) return;

    // Compte synchronisé : la vraie analyse existe, on retire le paramètre d'URL
    // (IAAnalysis lit aussi ?onboardingDemo=true et le ré-injecte à chaque
    // changement d'onglet — sans ce nettoyage, la démo s'auto-entretient).
    if (!accountsLoading && hasSyncedAccount) {
      const nextParams = new URLSearchParams(location.search);
      nextParams.delete('onboardingDemo');
      const nextSearch = nextParams.toString();
      navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
      return;
    }

    if (!isActive) {
      startDemo();
    }
  }, [location.pathname, location.search, isActive, startDemo, accountsLoading, hasSyncedAccount, navigate]);

  // Un trial sans compte connecté qui atterrit sur /ia-analysis "nu" (sans le
  // paramètre onboardingDemo) tombe sinon sur un état vide/verrouillé peu engageant :
  // on le redirige directement vers la démo IA, onglet recommandations. Comportement
  // volontairement identique desktop/mobile (contrairement au mur, cf. needsConnectWall).
  useEffect(() => {
    if (location.pathname !== '/ia-analysis') return;
    if (isDemoAnalysisRoute(location.pathname, location.search)) return;
    if (!needsAccountConnection) return;
    // Un compte réellement synchronisé signifie une vraie analyse IA possible :
    // jamais de redirection vers la démo dans ce cas, même si la checklist est périmée.
    if (hasSyncedAccount) return;
    navigate('/ia-analysis?onboardingDemo=true&tab=recommendations', { replace: true });
  }, [location.pathname, location.search, needsAccountConnection, hasSyncedAccount, navigate]);

  // Comptage des pages visitées (sidebar, navigation libre trial sans compte)
  useEffect(() => {
    if (isLocked || !shouldTrackExploration) return;
    if (isExemptExplorationPath(location.pathname, location.search)) return;
    registerExploration(`path:${location.pathname}`);
  }, [isLocked, shouldTrackExploration, location.pathname, location.search, registerExploration]);

  // Comptage des onglets sur l'analyse IA démo
  useEffect(() => {
    if (isLocked || !shouldTrackExploration) return;
    if (!isDemoAnalysisRoute(location.pathname, location.search)) return;

    const tab = new URLSearchParams(location.search).get('tab') || 'analysis';
    registerExploration(`ia-tab:${tab}`);
  }, [isLocked, shouldTrackExploration, location.pathname, location.search, registerExploration]);

  // Analytics : "mur démo affiché" — une seule capture par utilisateur, jamais
  // en boucle. localStorage évite toute ré-émission même si le mur clignote,
  // si plusieurs onglets/fenêtres ouvrent la route, ou après un reload.
  useEffect(() => {
    if (!uid || !wallVisible) return;

    const storageKey = demoWallShownStorageKey(uid);
    const alreadyCaptured = localStorage.getItem(storageKey) === 'true';
    wallShownCapturedRef.current = alreadyCaptured;
    if (alreadyCaptured) return;

    wallShownCapturedRef.current = true;
    localStorage.setItem(storageKey, 'true');
    capture('onboarding_demo_wall_shown', {
      pages_visited: pagesVisited,
      path: location.pathname,
    });
  }, [uid, wallVisible, pagesVisited, location.pathname]);

  const value = useMemo(
    () => ({
      isActive,
      isLocked,
      isLockResolved,
      pagesVisited,
      wallVisible,
      startDemo,
      endDemo,
      unlockDemo,
      connectAccount,
      importFile,
    }),
    [isActive, isLocked, isLockResolved, pagesVisited, wallVisible, startDemo, endDemo, unlockDemo, connectAccount, importFile],
  );

  return (
    <OnboardingDemoContext.Provider value={value}>
      {children}
    </OnboardingDemoContext.Provider>
  );
}

export function useOnboardingDemo() {
  const context = useContext(OnboardingDemoContext);
  if (context === undefined) {
    throw new Error('useOnboardingDemo must be used within OnboardingDemoProvider');
  }
  return context;
}

export function isOnboardingIaDemoSessionActive(uid: string | undefined): boolean {
  if (!uid) return false;
  return sessionStorage.getItem(demoActiveKey(uid)) === 'true';
}
